/**
 * SEO Utility Functions
 *
 * Helper functions for generating structured data (Schema.org JSON-LD)
 * and managing SEO metadata for landing pages.
 */

interface Organization {
  name: string;
  url: string;
  logo: string;
  description?: string;
}

interface SoftwareApplication {
  name: string;
  applicationCategory: string;
  operatingSystem: string;
  offers: {
    price: string;
    priceCurrency: string;
  };
  aggregateRating?: {
    ratingValue: number;
    reviewCount: number;
  };
}

interface FAQItem {
  question: string;
  answer: string;
}

/**
 * Generate Organization Schema
 * https://schema.org/Organization
 */
export function generateOrganizationSchema(org?: Partial<Organization>): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: org?.name || 'Sloty',
    url: org?.url || 'https://slotyapp.com',
    logo: org?.logo || 'https://slotyapp.com/logo.png',
    description: org?.description || 'Simple, powerful booking management for your business',
    sameAs: [
      'https://twitter.com/slotyapp',
      'https://facebook.com/slotyapp',
      'https://linkedin.com/company/slotyapp',
    ],
  };
}

/**
 * Generate SoftwareApplication Schema
 * https://schema.org/SoftwareApplication
 */
export function generateSoftwareSchema(app?: Partial<SoftwareApplication>): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: app?.name || 'Sloty',
    applicationCategory: app?.applicationCategory || 'BusinessApplication',
    operatingSystem: app?.operatingSystem || 'Web, iOS, Android',
    offers: {
      '@type': 'Offer',
      price: app?.offers?.price || '0',
      priceCurrency: app?.offers?.priceCurrency || 'USD',
    },
    ...(app?.aggregateRating && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: app.aggregateRating.ratingValue,
        reviewCount: app.aggregateRating.reviewCount,
      },
    }),
  };
}

/**
 * Generate FAQPage Schema
 * https://schema.org/FAQPage
 */
export function generateFAQSchema(faqs: FAQItem[]): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

/**
 * Generate BreadcrumbList Schema
 * https://schema.org/BreadcrumbList
 */
export function generateBreadcrumbSchema(items: Array<{ name: string; url: string }>): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

/**
 * Generate WebPage Schema
 * https://schema.org/WebPage
 */
export function generateWebPageSchema(params: {
  name: string;
  description: string;
  url: string;
}): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: params.name,
    description: params.description,
    url: params.url,
    inLanguage: ['en', 'pt-BR', 'pt-PT'],
    isPartOf: {
      '@type': 'WebSite',
      name: 'Sloty',
      url: 'https://slotyapp.com',
    },
  };
}

/**
 * Generate LocalBusiness Schema (for industry-specific pages)
 * https://schema.org/LocalBusiness
 */
export function generateLocalBusinessSchema(params: {
  businessType: string;
  name: string;
  description: string;
}): object {
  return {
    '@context': 'https://schema.org',
    '@type': params.businessType, // e.g., "MedicalBusiness", "HealthAndBeautyBusiness"
    name: params.name,
    description: params.description,
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Booking Management Services',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Online Appointment Booking',
          },
        },
      ],
    },
  };
}

/**
 * Combine multiple schemas into a graph
 */
export function combineSchemas(...schemas: object[]): object {
  return {
    '@context': 'https://schema.org',
    '@graph': schemas.map(s => { const { '@context': _, ...rest } = s as any; return rest; }),
  };
}

/**
 * Generate canonical URL
 */
export function generateCanonicalUrl(path: string): string {
  const baseUrl = 'https://slotyapp.com';
  return `${baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
}

/**
 * Generate Article Schema (for blog posts)
 * https://schema.org/Article
 */
export function generateArticleSchema(params: {
  headline: string;
  description: string;
  url: string;
  datePublished: string;
  dateModified?: string;
  author?: string;
  image?: string;
}): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: params.headline,
    description: params.description,
    url: params.url,
    datePublished: params.datePublished,
    dateModified: params.dateModified || params.datePublished,
    author: {
      '@type': 'Person',
      name: params.author || 'Sloty Team',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Sloty',
      url: 'https://slotyapp.com',
      logo: {
        '@type': 'ImageObject',
        url: 'https://slotyapp.com/logo.png',
      },
    },
    ...(params.image && {
      image: {
        '@type': 'ImageObject',
        url: params.image,
      },
    }),
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': params.url,
    },
  };
}

/**
 * Generate Open Graph image URL
 */
export function generateOGImageUrl(image: string): string {
  const baseUrl = 'https://slotyapp.com';
  if (image.startsWith('http')) {
    return image;
  }
  return `${baseUrl}${image.startsWith('/') ? image : `/${image}`}`;
}
